'use client'; // for server side rendering

import { forwardRef, type ElementType, type ReactNode } from 'react';
import { Box, styled } from '@mui/material';
import { useThemeProps } from '@mui/material/styles';
import { isThemeColor } from '../theme/theme-utils';

/**
 * Different variants represent different application level layouts:
 *
 *  - The "left-aligned" variant is a layout with a left aligned navigation menu. This
 *    is traditionally used for applications with several levels of navigation.
 *
 *  - The "center-aligned" variant is a standard, horizontally centered application. This
 *    is a fairly conventional layout used for content  rich websites.
 */
export type LayoutVariant = 'left-aligned' | 'center-aligned';

export interface ContentProps {
    children: ReactNode | ReactNode[];
    /**
     * If true, a `<main />` tag is used, which tells web scrapers and accessibility devices
     * that the content is of significant importance. There should only be a single `<main />
     * element per page.
     */
    main?: boolean;
    /**
     * The layout variant to use. Defaults to 'center-aligned'.
     */
    layout?: LayoutVariant;
    className?: string;
    bgcolor?: string;
}

interface ContentOwnerState {
    main?: boolean;
    layout?: LayoutVariant;
    bgcolor?: string;
}

const ContentRoot = styled(Box, {
    name: 'MuiContent',
    slot: 'root',
})<{ ownerState: ContentOwnerState; component?: ElementType }>(
    ({ theme, ownerState: { layout, bgcolor } }) => ({
        maxWidth: layout === 'center-aligned' ? `${theme.breakpoints.values.lg}px` : undefined,
        width: '100%',
        ...(layout === 'center-aligned' ? { margin: '0 auto' } : {}),
        padding: theme.spacing(3),
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        hyphens: 'auto',
        // if the user passes a key to the color theme, use the mapped color,
        // else treat the string as a normal color string
        background: bgcolor
            ? isThemeColor(theme, bgcolor)
                ? theme.color[bgcolor].hex
                : bgcolor
            : 'none',
        [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
            padding: theme.spacing(1.5),
        },
    })
);

export const Content = forwardRef<HTMLDivElement, ContentProps>(function Content(inProps, ref) {
    const props = useThemeProps({ props: inProps, name: 'MuiContent' });
    const { children, className, main, layout = 'center-aligned', bgcolor, ...other } = props;
    const ownerState: ContentOwnerState = { main, layout, bgcolor };
    return (
        <ContentRoot
            ref={ref}
            component={main ? 'main' : 'div'}
            className={className}
            ownerState={ownerState}
            {...other}>
            {children}
        </ContentRoot>
    );
});

export default Content;
